import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  MdWhatsapp, MdAdd, MdDelete, MdRefresh, MdCheckCircle, MdError,
  MdHourglassEmpty, MdPause, MdCode, MdInfoOutline, MdExpandMore,
  MdExpandLess, MdAutorenew
} from 'react-icons/md';
import { plantillasAPI, configuracionAPI } from '../services/api';
import { useModal } from '../context/ModalContext';

/* ─── Layout ─── */
const Container = styled.div`width: 100%; max-width: 100%;`;
const PageHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: ${p => p.theme.spacing.xl}; flex-wrap: wrap; gap: ${p => p.theme.spacing.md};
`;
const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl}; color: ${p => p.theme.colors.textPrimary}; margin: 0;
  display: flex; align-items: center; gap: 10px;
  svg { color: #25D366; }
`;
const HeaderActions = styled.div`display: flex; gap: 8px;`;
const RefreshBtn = styled.button`
  padding: 8px 14px; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md}; background: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textSecondary}; font-size: ${p => p.theme.fontSizes.sm};
  font-family: ${p => p.theme.fonts.primary}; display: flex; align-items: center; gap: 6px;
  cursor: pointer;
  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
`;

/* ─── Tabs ─── */
const TabBar = styled.div`
  display: flex; gap: 2px; margin-bottom: ${p => p.theme.spacing.xl};
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: 4px; box-shadow: ${p => p.theme.shadows.card}; width: fit-content;
`;
const Tab = styled.button`
  padding: 10px 20px; border: none; border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.active ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})` : 'transparent'};
  color: ${p => p.active ? 'white' : p.theme.colors.textSecondary};
  font-size: ${p => p.theme.fontSizes.sm}; font-weight: ${p => p.active ? 700 : 400};
  font-family: ${p => p.theme.fonts.primary}; cursor: pointer;
  display: flex; align-items: center; gap: 6px; transition: all 0.15s;
  svg { font-size: 1.1rem; }
`;

/* ─── Plantillas list ─── */
const TplGrid = styled.div`display: flex; flex-direction: column; gap: 12px;`;
const TplCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight}; border-radius: ${p => p.theme.borderRadius.lg};
  box-shadow: ${p => p.theme.shadows.card}; overflow: hidden;
`;
const TplHeader = styled.div`
  display: flex; align-items: center; gap: 14px; padding: 16px 20px; cursor: pointer;
  border-bottom: 1px solid ${p => p.open ? p.theme.colors.border : 'transparent'};
`;
const TplInfo = styled.div`flex: 1; min-width: 0;`;
const TplName = styled.div`
  font-size: ${p => p.theme.fontSizes.md}; font-weight: 700; color: ${p => p.theme.colors.textPrimary};
  font-family: monospace; margin-bottom: 3px;
`;
const TplMeta = styled.div`display: flex; gap: 8px; align-items: center; flex-wrap: wrap;`;
const Badge = styled.span`
  font-size: 11px; font-weight: 700; padding: 2px 9px; border-radius: 99px;
  background: ${p => p.bg}18; color: ${p => p.bg};
  border: 1px solid ${p => p.bg}30;
`;
const TplBody = styled.div`
  padding: ${p => p.open ? '16px 20px' : '0 20px'};
  max-height: ${p => p.open ? '600px' : '0'};
  overflow: hidden; transition: all 0.2s;
`;
const ComponentBlock = styled.div`
  margin-bottom: 12px;
`;
const CompLabel = styled.div`
  font-size: 10px; font-weight: 700; color: ${p => p.theme.colors.textLight};
  text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px;
`;
const CompText = styled.div`
  font-size: ${p => p.theme.fontSizes.sm}; color: ${p => p.theme.colors.textPrimary};
  background: ${p => p.theme.colors.background}; padding: 10px 14px;
  border-radius: ${p => p.theme.borderRadius.md}; white-space: pre-wrap; word-break: break-word;
  border: 1px solid ${p => p.theme.colors.border};
`;
const TplActions = styled.div`display: flex; justify-content: flex-end; gap: 8px; padding-top: 12px; border-top: 1px solid ${p => p.theme.colors.borderLight};`;
const DangerBtn = styled.button`
  padding: 6px 14px; border: 1px solid ${p => p.theme.colors.danger}40;
  border-radius: ${p => p.theme.borderRadius.md}; background: ${p => p.theme.colors.danger}10;
  color: ${p => p.theme.colors.danger}; font-size: ${p => p.theme.fontSizes.sm}; font-weight: 600;
  font-family: ${p => p.theme.fonts.primary}; display: flex; align-items: center; gap: 5px;
  cursor: pointer;
  &:hover { background: ${p => p.theme.colors.danger}20; }
`;

/* ─── Create form ─── */
const FormCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight}; border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl}; box-shadow: ${p => p.theme.shadows.card};
  max-width: 680px;
`;
const CardTitle = styled.h3`
  font-size: ${p => p.theme.fontSizes.lg}; font-weight: 700; color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 ${p => p.theme.spacing.lg} 0;
  padding-bottom: ${p => p.theme.spacing.md}; border-bottom: 1px solid ${p => p.theme.colors.border};
  display: flex; align-items: center; gap: 8px;
  svg { color: #25D366; }
`;
const Grid2 = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.lg};
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;
const FieldGroup = styled.div`display: flex; flex-direction: column; gap: 6px;`;
const Label = styled.label`
  font-size: ${p => p.theme.fontSizes.sm}; font-weight: 600; color: ${p => p.theme.colors.textPrimary};
  display: flex; align-items: center; gap: 6px;
`;
const Hint = styled.div`font-size: 11px; color: ${p => p.theme.colors.textLight}; margin-top: 2px;`;
const FInput = styled.input`
  padding: 10px 12px; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md}; font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.background}; color: ${p => p.theme.colors.textPrimary};
  font-family: ${p => p.value?.match(/^[a-z0-9_]+$/) ? 'monospace' : p.theme.fonts.primary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; font-family: ${p => p.theme.fonts.primary}; }
`;
const FSelect = styled.select`
  padding: 10px 12px; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md}; font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.background}; color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;
const FTextArea = styled.textarea`
  padding: 10px 12px; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md}; font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.background}; color: ${p => p.theme.colors.textPrimary};
  font-family: ${p => p.theme.fonts.primary}; resize: vertical;
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;
const VarChips = styled.div`display: flex; gap: 6px; flex-wrap: wrap; margin-top: 6px;`;
const VarChip = styled.button`
  padding: 3px 10px; border: 1px solid ${p => p.theme.colors.primary}40;
  border-radius: 99px; background: ${p => p.theme.colors.primary}10;
  color: ${p => p.theme.colors.primary}; font-size: 12px; font-family: monospace;
  cursor: pointer;
  &:hover { background: ${p => p.theme.colors.primary}20; }
`;
const WabaRow = styled.div`
  display: flex; gap: 8px; align-items: flex-end; margin-bottom: ${p => p.theme.spacing.lg};
`;
const AutoBtn = styled.button`
  padding: 10px 14px; border: 1px solid ${p => p.theme.colors.primary}50;
  border-radius: ${p => p.theme.borderRadius.md}; background: ${p => p.theme.colors.primary}10;
  color: ${p => p.theme.colors.primary}; font-size: ${p => p.theme.fontSizes.sm}; font-weight: 600;
  font-family: ${p => p.theme.fonts.primary}; display: flex; align-items: center; gap: 6px;
  cursor: pointer; white-space: nowrap;
  &:hover { background: ${p => p.theme.colors.primary}20; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const SubmitBtn = styled.button`
  width: 100%; padding: 12px;
  background: linear-gradient(135deg, #25D366, #128C7E);
  color: white; border: none; border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.md}; font-weight: 700;
  font-family: ${p => p.theme.fonts.primary}; display: flex; align-items: center;
  justify-content: center; gap: 8px; cursor: pointer; margin-top: ${p => p.theme.spacing.lg};
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { transform: translateY(-1px); }
`;
const InfoBox = styled.div`
  background: ${p => p.theme.colors.primary}08; border: 1px solid ${p => p.theme.colors.primary}25;
  border-radius: ${p => p.theme.borderRadius.md}; padding: 12px 16px;
  font-size: ${p => p.theme.fontSizes.sm}; color: ${p => p.theme.colors.textSecondary};
  display: flex; gap: 10px; margin-bottom: ${p => p.theme.spacing.lg};
  svg { color: ${p => p.theme.colors.primary}; font-size: 1.1rem; flex-shrink: 0; margin-top: 1px; }
`;
const EmptyState = styled.div`
  text-align: center; padding: ${p => p.theme.spacing.xl} 0;
  color: ${p => p.theme.colors.textLight}; font-size: ${p => p.theme.fontSizes.sm};
  svg { font-size: 2.5rem; display: block; margin: 0 auto 12px; opacity: 0.3; }
`;

const STATUS_CONF = {
  approved: { label: 'Aprobada', color: '#25D366', Icon: MdCheckCircle },
  pending:  { label: 'Pendiente', color: '#F6AD55', Icon: MdHourglassEmpty },
  rejected: { label: 'Rechazada', color: '#FC8181', Icon: MdError },
  paused:   { label: 'Pausada', color: '#A0AEC0', Icon: MdPause },
};
const CAT_COLOR = { MARKETING: '#63B3ED', UTILITY: '#68D391', AUTHENTICATION: '#F6AD55' };

const extractVars = (text) => {
  if (!text) return [];
  const matches = [...new Set([...text.matchAll(/\{\{(\d+)\}\}/g)].map(m => m[1]))];
  return matches.sort((a, b) => Number(a) - Number(b));
};

/* ════════════════════════════════════════════════════════ */
const CobranzaCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight}; border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl}; box-shadow: ${p => p.theme.shadows.card}; max-width: 560px;
`;
const CobranzaSelect = styled.select`
  width: 100%; padding: 10px 12px; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md}; font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.background}; color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  margin-top: 6px;
`;
const SaveCobranzaBtn = styled.button`
  padding: 10px 24px; background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white; border: none; border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm}; font-weight: 700; font-family: ${p => p.theme.fonts.primary};
  cursor: pointer; margin-top: ${p => p.theme.spacing.lg};
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { transform: translateY(-1px); }
`;

const Plantillas = () => {
  const { toast, confirm } = useModal();
  const [activeTab, setActiveTab] = useState('lista');
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const [form, setForm] = useState({
    wabaId: '', nombre: '', idioma: 'es_MX', categoria: 'MARKETING',
    encabezado: '', cuerpo: '', pie: '',
  });
  const [bodyEjemplos, setBodyEjemplos] = useState({}); // { varIndex: exampleString }
  const [guardando, setGuardando] = useState(false);
  const [fetchingWaba, setFetchingWaba] = useState(false);

  // Cobranza automation
  const [cobranzaNombre, setCobranzaNombre]   = useState('');
  const [cobranzaIdioma, setCobranzaIdioma]   = useState('es_MX');
  const [savingCobranza, setSavingCobranza]   = useState(false);

  const cargar = async () => {
    setLoading(true);
    try { const r = await plantillasAPI.getAll(); setPlantillas(r.data.data || []); }
    catch (err) { toast(err.message || 'Error al cargar plantillas', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    cargar();
    configuracionAPI.getConfiguracion()
      .then(r => {
        const d = r.data.data || {};
        if (d.ycloud_plantilla_nombre) setCobranzaNombre(d.ycloud_plantilla_nombre);
        if (d.ycloud_plantilla_idioma) setCobranzaIdioma(d.ycloud_plantilla_idioma);
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  const handleSaveCobranza = async () => {
    setSavingCobranza(true);
    try {
      const r = await configuracionAPI.getConfiguracion();
      const current = r.data.data || {};
      await configuracionAPI.updateConfiguracion({
        ...current,
        ycloud_plantilla_nombre: cobranzaNombre,
        ycloud_plantilla_idioma: cobranzaIdioma,
      });
      toast('Plantilla de cobranza guardada', 'success');
    } catch (err) { toast(err.message || 'Error al guardar', 'error'); }
    finally { setSavingCobranza(false); }
  };

  const handleAutoWabaId = async () => {
    setFetchingWaba(true);
    try {
      const r = await plantillasAPI.getWabaId();
      setForm(p => ({ ...p, wabaId: r.data.wabaId }));
      toast('WABA ID detectado automáticamente', 'success');
    } catch (err) { toast(err.message || 'No se pudo detectar. Ingrésalo manualmente.', 'error'); }
    finally { setFetchingWaba(false); }
  };

  const insertVar = (field, n) => {
    setForm(prev => ({ ...prev, [field]: (prev[field] || '') + `{{${n}}}` }));
  };

  const handleCreate = async () => {
    if (!form.wabaId.trim()) { toast('El WABA ID es obligatorio', 'error'); return; }
    if (!form.nombre.trim()) { toast('El nombre es obligatorio', 'error'); return; }
    if (!form.cuerpo.trim()) { toast('El cuerpo es obligatorio', 'error'); return; }
    setGuardando(true);
    try {
      // Build ordered body_ejemplos array: one value per {{N}} variable, sorted by index
      const varIndices = extractVars(form.cuerpo).map(Number).sort((a, b) => a - b);
      const body_ejemplos = varIndices.map(idx => bodyEjemplos[idx] || '');
      await plantillasAPI.create({ ...form, body_ejemplos });
      toast('Plantilla enviada para revisión a WhatsApp. Aparecerá como "Pendiente" hasta ser aprobada.', 'success');
      setForm({ wabaId: form.wabaId, nombre: '', idioma: 'es_MX', categoria: 'MARKETING', encabezado: '', cuerpo: '', pie: '' });
      setBodyEjemplos({});
      setActiveTab('lista');
      cargar();
    } catch (err) { toast(err.message || 'Error al crear plantilla', 'error'); }
    finally { setGuardando(false); }
  };

  const handleDelete = async (tpl) => {
    const ok = await confirm(
      `¿Eliminar la plantilla "${tpl.name}"? Esta acción no se puede deshacer.`,
      { title: 'Eliminar plantilla', confirmText: 'Eliminar', danger: true }
    );
    if (!ok) return;
    try {
      await plantillasAPI.delete(tpl.wabaId, tpl.name);
      setPlantillas(prev => prev.filter(p => p.id !== tpl.id));
      toast('Plantilla eliminada', 'success');
    } catch (err) { toast(err.message || 'Error al eliminar', 'error'); }
  };

  const getBody = (tpl) => tpl.components?.find(c => c.type === 'BODY')?.text || '';
  const getHeader = (tpl) => tpl.components?.find(c => c.type === 'HEADER');
  const getFooter = (tpl) => tpl.components?.find(c => c.type === 'FOOTER');
  const cuerpoVars = extractVars(form.cuerpo);
  const nextVar = cuerpoVars.length > 0 ? Math.max(...cuerpoVars.map(Number)) + 1 : 1;
  const varChips = [...new Set([...cuerpoVars.map(Number), nextVar])].sort((a, b) => a - b);

  return (
    <Container>
      <PageHeader>
        <Title><MdWhatsapp /> Plantillas WhatsApp</Title>
        <HeaderActions>
          <RefreshBtn onClick={cargar}><MdRefresh /> Actualizar</RefreshBtn>
        </HeaderActions>
      </PageHeader>

      <TabBar>
        <Tab active={activeTab === 'lista'} onClick={() => setActiveTab('lista')}>
          <MdWhatsapp /> Mis plantillas {plantillas.length > 0 && `(${plantillas.length})`}
        </Tab>
        <Tab active={activeTab === 'nueva'} onClick={() => setActiveTab('nueva')}>
          <MdAdd /> Nueva plantilla
        </Tab>
      </TabBar>

      {/* ════ LISTA ════ */}
      {activeTab === 'lista' && (
        <>
          {loading && <EmptyState><MdAutorenew />Cargando desde yCloud...</EmptyState>}
          {!loading && plantillas.length === 0 && (
            <EmptyState>
              <MdWhatsapp />
              Sin plantillas registradas. Crea una nueva o verifica tu API Key en Configuración.
            </EmptyState>
          )}
          <TplGrid>
            {plantillas.map(tpl => {
              const open = expandedId === tpl.id || expandedId === tpl.name;
              const key = tpl.id || tpl.name;
              const statusConf = STATUS_CONF[tpl.status?.toLowerCase()] || STATUS_CONF.pending;
              const body = getBody(tpl);
              const header = getHeader(tpl);
              const footer = getFooter(tpl);
              const vars = extractVars(body);
              return (
                <TplCard key={key}>
                  <TplHeader onClick={() => setExpandedId(open ? null : key)} open={open}>
                    <MdCode style={{ color: '#25D366', fontSize: '1.4rem', flexShrink: 0 }} />
                    <TplInfo>
                      <TplName>{tpl.name}</TplName>
                      <TplMeta>
                        <Badge bg={statusConf.color}><statusConf.Icon style={{ fontSize: 10, marginRight: 3 }} />{statusConf.label}</Badge>
                        <Badge bg={CAT_COLOR[tpl.category] || '#aaa'}>{tpl.category}</Badge>
                        <Badge bg="#A0AEC0">{tpl.language}</Badge>
                        {vars.length > 0 && <Badge bg="#63B3ED">{vars.length} variable{vars.length !== 1 ? 's' : ''}</Badge>}
                      </TplMeta>
                    </TplInfo>
                    {open ? <MdExpandLess style={{ color: '#aaa' }} /> : <MdExpandMore style={{ color: '#aaa' }} />}
                  </TplHeader>
                  <TplBody open={open}>
                    {header && (
                      <ComponentBlock>
                        <CompLabel>Encabezado ({header.format})</CompLabel>
                        <CompText>{header.text || '[Multimedia]'}</CompText>
                      </ComponentBlock>
                    )}
                    <ComponentBlock>
                      <CompLabel>Cuerpo</CompLabel>
                      <CompText>{body}</CompText>
                    </ComponentBlock>
                    {footer && (
                      <ComponentBlock>
                        <CompLabel>Pie</CompLabel>
                        <CompText>{footer.text}</CompText>
                      </ComponentBlock>
                    )}
                    {tpl.status?.toLowerCase() === 'rejected' && tpl.qualityScore?.reasons?.length > 0 && (
                      <InfoBox>
                        <MdError />
                        <div>
                          <strong>Motivo de rechazo:</strong>{' '}
                          {tpl.qualityScore.reasons.join(', ')}
                        </div>
                      </InfoBox>
                    )}
                    <TplActions>
                      <DangerBtn onClick={() => handleDelete(tpl)}>
                        <MdDelete /> Eliminar
                      </DangerBtn>
                    </TplActions>
                  </TplBody>
                </TplCard>
              );
            })}
          </TplGrid>

          {/* ════ COBRANZA AUTOMÁTICA ════ */}
          <CobranzaCard style={{ marginTop: 24 }}>
            <CardTitle><MdCheckCircle style={{ color: '#25D366' }} /> Plantilla de cobranza</CardTitle>

            <FieldGroup style={{ marginBottom: 16 }}>
              <Label>Plantilla de cobranza</Label>
              <CobranzaSelect
                value={cobranzaNombre}
                onChange={e => {
                  const tpl = plantillas.find(p => p.name === e.target.value);
                  setCobranzaNombre(e.target.value);
                  if (tpl?.language) setCobranzaIdioma(tpl.language);
                }}
              >
                <option value="">— Sin plantilla asignada —</option>
                {plantillas.filter(p => p.status?.toLowerCase() === 'approved').map(p => (
                  <option key={p.id || p.name} value={p.name}>
                    {p.name} ({p.language})
                  </option>
                ))}
              </CobranzaSelect>
              {plantillas.filter(p => p.status?.toLowerCase() === 'approved').length === 0 && (
                <Hint>No hay plantillas aprobadas aún. Crea y espera la aprobación de una plantilla primero.</Hint>
              )}
            </FieldGroup>

            {cobranzaNombre && (
              <FieldGroup style={{ marginBottom: 16 }}>
                <Label>Idioma detectado</Label>
                <FInput value={cobranzaIdioma} readOnly style={{ background: 'transparent', color: '#888' }} />
                <Hint>
                  La plantilla de cobranza debe tener 4 variables: {'{{1}}'} nombre, {'{{2}}'} recibo, {'{{3}}'} monto, {'{{4}}'} vencimiento.
                </Hint>
              </FieldGroup>
            )}

            <SaveCobranzaBtn onClick={handleSaveCobranza} disabled={savingCobranza}>
              {savingCobranza ? 'Guardando...' : 'Guardar asignación'}
            </SaveCobranzaBtn>
          </CobranzaCard>
        </>
      )}

      {/* ════ NUEVA PLANTILLA ════ */}
      {activeTab === 'nueva' && (
        <FormCard>
          <CardTitle><MdWhatsapp /> Nueva plantilla</CardTitle>

          <InfoBox>
            <MdInfoOutline />
            <div>
              Las plantillas deben ser aprobadas por Meta/WhatsApp antes de poder usarse. El proceso toma entre 1 y 24 horas. Evita usar URLs, precios, emojis excesivos o lenguaje agresivo para aumentar las probabilidades de aprobación.
            </div>
          </InfoBox>

          {/* WABA ID */}
          <FieldGroup style={{ marginBottom: 20 }}>
            <Label>WABA ID (WhatsApp Business Account ID)</Label>
            <WabaRow>
              <FInput
                style={{ flex: 1 }}
                placeholder="Ej: 123456789012345"
                value={form.wabaId}
                onChange={e => setForm(p => ({ ...p, wabaId: e.target.value }))}
              />
              <AutoBtn onClick={handleAutoWabaId} disabled={fetchingWaba}>
                <MdAutorenew /> {fetchingWaba ? 'Buscando...' : 'Detectar auto'}
              </AutoBtn>
            </WabaRow>
            <Hint>Lo encuentras en Meta Business Suite → Configuración → Cuentas de WhatsApp Business.</Hint>
          </FieldGroup>

          <Grid2>
            <FieldGroup>
              <Label>Nombre de la plantilla</Label>
              <FInput
                placeholder="Ej: recordatorio_pago"
                value={form.nombre}
                onChange={e => setForm(p => ({ ...p, nombre: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))}
              />
              <Hint>Solo minúsculas, números y guiones bajos.</Hint>
            </FieldGroup>
            <FieldGroup>
              <Label>Idioma</Label>
              <FSelect value={form.idioma} onChange={e => setForm(p => ({ ...p, idioma: e.target.value }))}>
                <option value="es_MX">Español (México)</option>
                <option value="es">Español</option>
                <option value="en_US">English (US)</option>
                <option value="pt_BR">Português (BR)</option>
              </FSelect>
            </FieldGroup>
            <FieldGroup>
              <Label>Categoría</Label>
              <FSelect value={form.categoria} onChange={e => setForm(p => ({ ...p, categoria: e.target.value }))}>
                <option value="MARKETING">Marketing</option>
                <option value="UTILITY">Utilidad (avisos, confirmaciones)</option>
                <option value="AUTHENTICATION">Autenticación (OTP)</option>
              </FSelect>
            </FieldGroup>
          </Grid2>

          <FieldGroup style={{ marginBottom: 16 }}>
            <Label>Encabezado <span style={{ fontWeight: 400, color: '#aaa', fontSize: 11 }}>(opcional)</span></Label>
            <FInput
              placeholder="Ej: Aviso importante"
              value={form.encabezado}
              onChange={e => setForm(p => ({ ...p, encabezado: e.target.value }))}
            />
          </FieldGroup>

          <FieldGroup style={{ marginBottom: 8 }}>
            <Label>Cuerpo del mensaje <span style={{ color: 'red' }}>*</span></Label>
            <FTextArea
              rows={5}
              placeholder={'Ej: Hola {{1}}, te recordamos que tienes un saldo pendiente de ${{2}}. Por favor realiza tu pago antes del {{3}}.'}
              value={form.cuerpo}
              onChange={e => setForm(p => ({ ...p, cuerpo: e.target.value }))}
            />
            <Hint>Usa {'{{1}}'}, {'{{2}}'}, etc. para variables que se llenarán al enviar.</Hint>
          </FieldGroup>
          <VarChips>
            {varChips.map(n => (
              <VarChip
                key={n}
                type="button"
                onClick={() => insertVar('cuerpo', n)}
                title={`Insertar {{${n}}}`}
                style={n === nextVar ? { borderStyle: 'dashed', opacity: 0.7 } : {}}
              >
                {n === nextVar ? `+ {{${n}}}` : `{{${n}}}`}
              </VarChip>
            ))}
          </VarChips>

          {cuerpoVars.length > 0 && (
            <div style={{ marginTop: 12, marginBottom: 4, padding: '12px 14px', background: 'rgba(99,179,237,0.07)', border: '1px solid rgba(99,179,237,0.3)', borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, fontSize: 12, fontWeight: 700, color: '#63B3ED', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <MdInfoOutline style={{ fontSize: 14 }} />
                Ejemplos de variables (requerido por WhatsApp para aprobar)
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {cuerpoVars.map(v => (
                  <div key={v} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#63B3ED', width: 36, flexShrink: 0 }}>{`{{${v}}}`}</span>
                    <FInput
                      placeholder={`Ejemplo para {{${v}}}`}
                      value={bodyEjemplos[v] || ''}
                      onChange={e => setBodyEjemplos(prev => ({ ...prev, [v]: e.target.value }))}
                      style={{ flex: 1, fontSize: 12, padding: '5px 10px' }}
                    />
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 8 }}>
                Muestra a WhatsApp qué tipo de contenido irá en cada variable. Se usan solo para revisión, no para el envío real.
              </div>
            </div>
          )}

          <FieldGroup style={{ marginTop: 12 }}>
            <Label>Pie de página <span style={{ fontWeight: 400, color: '#aaa', fontSize: 11 }}>(opcional)</span></Label>
            <FInput
              placeholder="Ej: Este mensaje fue enviado por Empresa SA"
              value={form.pie}
              onChange={e => setForm(p => ({ ...p, pie: e.target.value }))}
            />
          </FieldGroup>

          <SubmitBtn onClick={handleCreate} disabled={guardando}>
            <MdWhatsapp /> {guardando ? 'Enviando a WhatsApp...' : 'Enviar para aprobación'}
          </SubmitBtn>
        </FormCard>
      )}

    </Container>
  );
};

export default Plantillas;
